import { Input, Select, cn, type SelectProps } from '@chopchop-se/ui';
import type { InputHTMLAttributes, ReactNode } from 'react';
import { forwardRef, useId } from 'react';

/**
 * Labelled field wrappers for the register forms.
 *
 * Why these exist instead of `@chopchop-se/ui`'s `FormField`/`Select` directly:
 *
 * 1. **Label association.** The design system renders `<label>` without
 *    `htmlFor`, so a click on the label does not focus the field and a screen
 *    reader announces an unnamed input. These wrappers own an id and wire it
 *    up. (Reported upstream — see the module report; the visible custom-select
 *    trigger still needs a fix inside the library.)
 * 2. **Hint placement.** The library's `hint` slot sits top-RIGHT of the label,
 *    which is right for "forgot password?" and wrong for a sentence explaining
 *    what "Fångstvikt" does. Here hints go under the control, where long
 *    Swedish help text belongs.
 *
 * `lockedReason` is the "Synkad från BC" state: disabled AND explained,
 * because a greyed-out field with no reason is the most common "the system is
 * broken" support call.
 */

export function FormSection({
  title,
  description,
  children,
  className,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  className?: string;
}): ReactNode {
  return (
    <section className={cn('flex flex-col gap-4', className)}>
      <div>
        <h3 className="font-mono text-xs uppercase tracking-[0.14em] text-ink-muted">{title}</h3>
        {description ? <p className="mt-1 text-sm text-ink-muted">{description}</p> : null}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">{children}</div>
    </section>
  );
}

const LABEL_CLS = 'mb-2 block text-[0.9375rem] font-medium tracking-[0.01em] text-ink-muted';

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label: string;
  hint?: string;
  error?: string;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, hint, error, className, ...rest },
  ref,
) {
  const id = useId();
  return (
    <div className={cn('flex flex-col', className)}>
      <label className={LABEL_CLS} htmlFor={id}>
        {label}
      </label>
      <Input id={id} ref={ref} variant="outline" invalid={Boolean(error)} {...rest} />
      {error ? <p className="mt-1 text-xs text-danger">{error}</p> : null}
      {hint && !error ? <p className="mt-1 text-xs text-ink-muted">{hint}</p> : null}
    </div>
  );
});

export type SelectFieldProps = Omit<SelectProps, 'label' | 'hint'> & {
  label: string;
  hint?: string;
};

export function SelectField({ label, hint, error, className, ...rest }: SelectFieldProps): ReactNode {
  const id = useId();
  return (
    <div className={cn('flex flex-col', className)}>
      <label className={LABEL_CLS} htmlFor={id}>
        {label}
      </label>
      {/* `id` reaches the library's hidden native <select>, which is what forms
          and assistive tech actually read from. */}
      <Select id={id} error={error} {...rest} />
      {error ? <p className="mt-1 text-xs text-danger">{error}</p> : null}
      {hint && !error ? <p className="mt-1 text-xs text-ink-muted">{hint}</p> : null}
    </div>
  );
}

export interface CheckboxFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label: string;
  hint?: string;
  /** Rendered under the label when the field is BC-owned. */
  lockedReason?: string;
}

export const CheckboxField = forwardRef<HTMLInputElement, CheckboxFieldProps>(function CheckboxField(
  { label, hint, lockedReason, className, disabled, ...rest },
  ref,
) {
  const id = useId();
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <div className="flex items-start gap-2.5">
        <input
          id={id}
          ref={ref}
          type="checkbox"
          disabled={disabled}
          className="mt-0.5 size-4 shrink-0 rounded border-surface-border text-brand-600 focus-visible:ring-2 focus-visible:ring-brand-600/40 disabled:opacity-50"
          {...rest}
        />
        <label htmlFor={id} className="min-w-0 text-sm text-ink">
          {label}
        </label>
      </div>
      {hint ? <p className="pl-6.5 text-xs text-ink-muted">{hint}</p> : null}
      {lockedReason ? <p className="pl-6.5 text-xs text-ink-muted">{lockedReason}</p> : null}
    </div>
  );
});

/** A read-only key/value line for detail panels. */
export function ReadOnlyField({ label, value }: { label: string; value: ReactNode }): ReactNode {
  return (
    <div>
      <p className="mb-1 font-mono text-xs uppercase tracking-[0.14em] text-ink-muted">{label}</p>
      <div className="text-sm text-ink">{value === null || value === undefined || value === '' ? '—' : value}</div>
    </div>
  );
}
